import React from "react";
import { styled, useTheme } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  useMediaQuery,
} from "@mui/material";
import { Carousel } from "react-responsive-carousel";
import { isMobile, isTablet, isIOS, isAndroid } from "react-device-detect";
import { v4FlatWinsAtBreakpointsSx } from "../../../theme/v4CompatSx";
import appleappstore from "../../Modals/app-store-logo.png";
import googleplaystore from "../../Modals/google-play-logo.png";

/**
 * #1088 — the `hero` layout of the shared `InstallApp`: the home page's
 * "Install The Free App" block (left column: icon, headline, device-aware
 * INSTALL THE APP button, store badges; right column: phone skin with the
 * screenshot carousel). Also the /app hero, with a sub-line under the headline.
 *
 * Converted from `Home/App.js` (MUI v4 + `makeStyles`) to MUI v5+ `styled`/`sx`
 * per docs/mui-conversion-guide.md, keeping every value. The phone-skin column
 * follows the already-converted `FreeTrial/Trial.tsx`, which is the same
 * construct. Conventions:
 *
 * - Bare elements (`div`, `img`) → `styled()`, which is raw emotion, so their
 *   literal `@media (min-width:769px)` blocks keep their v4 position.
 * - MUI components → `sx`. The two `centerBox*` rules drop their 769 block
 *   (proof at `heroCenterBoxLeftSx`); the three `Box` rules whose ONLY
 *   breakpoint is 769 keep it, because with no theme breakpoint in the same
 *   object there is nothing for the appended block to land after.
 * - `h2` has no breakpoint rules in the theme, so the headline sizes port
 *   flat. `h4` declares fontSize/lineHeight at `sm` and `md`, and Button
 *   `sizeLarge` declares fontSize at `sm` and `md`, so those flat values are
 *   re-stated with `v4FlatWinsAtBreakpointsSx` (guide §2), as /free-trial does.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `titleOld`, `button`, `buttonSubText`,
 * `noUnderline`, `buttonContainer` were never referenced from the JSX;
 * `dashboardScroller` carried a malformed `"@media (min-width:960px"` key with
 * an empty body; empty `"@media (min-width:960px)": {}` blocks rendered nothing.
 */

/* ---------------------------------------------------------------------- *
 * styled() — bare host elements, which have no `sx` prop
 * ---------------------------------------------------------------------- */

// was: Home/App.js useStyles().background
const Background = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.ffNavy,
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  width: "auto",
  display: "flex",
  height: "auto",
  [theme.breakpoints.up("sm")]: {
    height: "auto",
  },
  "@media (min-width:769px)": {
    height: "auto",
  },
  [theme.breakpoints.up("md")]: {
    height: "100vh",
  },
  [theme.breakpoints.up("lg")]: {
    height: "80vh",
    maxHeight: "880px",
  },
}));

// was: useStyles().appIconGraphic — a bare <img>. `borderRadius` spelled with
// its unit (see the note on the footer layout's AppIconGraphic).
const AppIconGraphic = styled("img")(({ theme }) => ({
  width: "80px",
  borderRadius: "10px",
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    width: "110px",
    marginBottom: theme.spacing(1),
  },
}));

// was: useStyles().appStoreLogo — a bare <img>, rendered twice.
const AppStoreLogo = styled("img")(({ theme }) => ({
  maxWidth: "140px",
  margin: "5px",
  textAlign: "center",
  [theme.breakpoints.up("sm")]: {
    margin: "10px 5px",
    maxWidth: "130px",
  },
  [theme.breakpoints.up("md")]: {
    margin: "10px 5px",
    maxWidth: "160px",
  },
}));

// was: useStyles().skinWrapper
const SkinWrapper = styled("div")({
  position: "relative",
});

// was: useStyles().appImage
const AppImage = styled("img")(({ theme }) => ({
  minHeight: "500px",
  maxHeight: "700px",
  marginBottom: theme.spacing(5),
  [theme.breakpoints.up("md")]: {
    marginBottom: theme.spacing(0),
  },
}));

// was: useStyles().appImageHalf
const AppImageHalf = styled("img")(({ theme }) => ({
  minHeight: "250px",
  maxHeight: "450px",
  marginBottom: theme.spacing(0),
  [theme.breakpoints.up("md")]: {
    marginBottom: theme.spacing(0),
  },
}));

// was: useStyles().dashboardScroller (its malformed, empty
// `"@media (min-width:960px"` key is dropped — JSS never emitted it).
const DashboardScroller = styled("div")(({ theme }) => ({
  position: "absolute",
  top: "64px",
  left: "-1px",
  height: "365px",
  width: "258px",
  padding: "7% 7% 0 7%",
  overflow: "hidden",
  [theme.breakpoints.up("md")]: {
    width: "296px",
    top: "95px",
    left: "22px",
    padding: "0",
    height: "580px",
  },
}));

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

// was: useStyles().centerBoxLeft
const centerBoxLeftSx = (theme: Theme) => ({
  position: "relative",
  height: "auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  flexDirection: "column",
  marginTop: "40px",
  [theme.breakpoints.up("sm")]: {
    alignItems: "flex-start",
    height: "auto",
    marginTop: "40px",
  },
  // PARITY RESTORE — v4's `@media (min-width:769px) { height: auto }` is
  // dropped, not ported: in an `sx` object a literal `@media` key is appended
  // AFTER every theme breakpoint block, so it would beat the `md`/`lg` heights
  // at every desktop width (guide §2, "`sx` reorders its own keys"; measured on
  // the identical construct in `FreeTrial/Top.tsx`). It is provably inert in v4:
  // below 769 it does not apply; from 769 to 959 it re-asserts the `height:
  // auto` the flat rule and the `sm` block already set; from 960 up v4's later
  // `md`/`lg` blocks overrode it.
  [theme.breakpoints.up("md")]: {
    height: "100vh",
    alignItems: "flex-start",
    marginTop: "0px",
  },
  [theme.breakpoints.up("lg")]: {
    height: "80vh",
    maxHeight: "880px",
  },
});

// was: useStyles().centerBoxRight
const centerBoxRightSx = (theme: Theme) => ({
  position: "relative",
  height: "auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  marginTop: "16px",
  [theme.breakpoints.up("sm")]: {
    height: "auto",
    marginTop: "32px",
  },
  // PARITY RESTORE — same dropped-`@media (min-width:769px)` case as
  // `centerBoxLeftSx` above.
  [theme.breakpoints.up("md")]: {
    height: "100vh",
    alignItems: "flex-start",
    marginTop: "0px",
  },
  [theme.breakpoints.up("lg")]: {
    height: "80vh",
    maxHeight: "880px",
  },
});

// was: useStyles().appTitleContainer — only breakpoint is the literal 769.
const appTitleContainerSx = {
  display: "flex",
  flexDirection: "row",
  "@media (min-width:769px)": {
    flexDirection: "column",
  },
} as const;

// was: useStyles().appIcon — only breakpoint is the literal 769.
const appIconSx = {
  marginRight: "16px",
  "@media (min-width:769px)": {
    marginRight: "0px",
  },
} as const;

// was: useStyles().title
const titleSx = (theme: Theme) => ({
  letterSpacing: "-2px",
  fontSize: "2rem",
  lineHeight: "1.8rem",
  fontWeight: 600,
  textShadow: "-2px 2px 2px #171717",
  "& span": {
    color: "background.ffGreen",
  },
  [theme.breakpoints.up("sm")]: {
    fontSize: "2.6rem",
    lineHeight: "2.4rem",
  },
  [theme.breakpoints.up("lg")]: {
    fontSize: "3rem",
    lineHeight: "3.2rem",
  },
});

// was: useStyles().titleLarge
const titleLargeSx = (theme: Theme) => ({
  letterSpacing: "-2px",
  fontSize: "2.8rem",
  lineHeight: "2.8rem",
  fontWeight: 600,
  textShadow: "-2px 2px 2px #171717",
  [theme.breakpoints.up("sm")]: {
    fontSize: "4.2rem",
    lineHeight: "4rem",
  },
  [theme.breakpoints.up("lg")]: {
    fontSize: "5.4rem",
    lineHeight: "5rem",
  },
});

// The /app sub-line ("Watch Anytime. Watch Anywhere."). Sizes and colour are
// the ones /app's old hero gave this line (`Banners/SuccessAppTrial.js`
// `appSubTitle`); it sits under the headline, so it takes the headline's
// alignment instead of the old centred one. `h3` declares fontSize/lineHeight
// at `sm` and `md`, and this rule declares both at both, so it wins there.
const sublineSx = (theme: Theme) => ({
  fontSize: "1.2rem",
  marginTop: "8px",
  marginBottom: "16px",
  color: theme.palette.background.ffGreen,
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
  },
});

// was: useStyles().downloadTextContainer — only breakpoint is the literal 769.
const downloadTextContainerSx = {
  marginTop: "16px",
  textAlign: "center",
  width: "100%",
  "@media (min-width:769px)": {
    textAlign: "left",
    width: "auto",
  },
} as const;

// was: useStyles().installAppButton
// `color` is named from the theme (v4's rule already did). PARITY RESTORE —
// the flat `fontSize` would lose to the theme's `sizeLarge` fontSize at `sm`
// and `md` (guide §2, "A flat `sx` declaration loses to the component's own
// breakpoint rule"), so it is re-stated there; the file's own `sm` block stays
// on top.
const installAppButtonSx = (theme: Theme) =>
  v4FlatWinsAtBreakpointsSx(
    theme,
    {
      width: "90%",
      margin: "12px 5% 16px 5%",
      fontSize: "1.2rem",
      border: "2px solid #fff",
      color: theme.palette.common.white,
      padding: "16px",
      "&:hover": {
        border: "2px solid #99ca3c",
        color: theme.palette.background.ffGreen,
      },
      [theme.breakpoints.up("sm")]: {
        width: "auto",
        margin: "32px 0 16px 0",
        padding: "16px 48px",
      },
    },
    ["fontSize"],
  );

// was: useStyles().downloadText
// PARITY RESTORE — flat-only rule on a `<Typography variant="h4">`, whose
// theme tokens set fontSize/lineHeight again at `sm` and `md` (same case and
// helper as the footer layout's `downloadTextSx`).
const downloadTextSx = (theme: Theme) =>
  v4FlatWinsAtBreakpointsSx(
    theme,
    {
      letterSpacing: "-1px",
      fontSize: "1rem",
      lineHeight: "1.2rem",
      fontWeight: 400,
      textShadow: "-2px 2px 2px #171717",
      marginBottom: "8px",
      color: "#ddd",
    },
    ["fontSize", "lineHeight"],
  );

// was: useStyles().purchaseText — PARITY RESTORE, same case as downloadTextSx.
const purchaseTextSx = (theme: Theme) =>
  v4FlatWinsAtBreakpointsSx(
    theme,
    {
      letterSpacing: "-1px",
      fontSize: "1rem",
      lineHeight: "1.2rem",
      fontWeight: 500,
      textShadow: "-2px 2px 2px #171717",
      marginTop: "16px",
      marginBottom: "8px",
      color: "#ddd",
    },
    ["fontSize", "lineHeight"],
  );

// was: useStyles().imageContainer
const imageContainerSx = (theme: Theme) => ({
  height: "365px",
  [theme.breakpoints.up("sm")]: {
    height: "515px",
  },
});

export type HeroLinks = {
  /** INSTALL THE APP on an iPhone or iPad. */
  ios: string;
  /** INSTALL THE APP on an Android phone or tablet. */
  android: string;
  /** The App Store badge. */
  appStore: string;
  /** The Google Play badge. */
  playStore: string;
};

const HeroLayout = ({
  hrefs,
  subline,
}: {
  hrefs: HeroLinks;
  subline?: string;
}) => {
  const theme = useTheme();

  // v4's <Hidden smDown> / <Hidden mdUp> pair was complementary, so one query
  // drives both branches (guide §2, "Complementary <Hidden> pairs"): exactly
  // one phone skin renders at every width and with no matchMedia (jsdom).
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));

  // Phones and tablets get the INSTALL THE APP button; everything else
  // (desktop browsers AND smart-TV browsers, where react-device-detect reports
  // isMobile, isTablet and isBrowser all false) gets the badges only. Keep this
  // an if/else with no third case: a missing branch once blanked the whole
  // site on smart TVs (AppLinks.smarttv.test.js).
  const phoneOrTablet = isMobile || isTablet;

  return (
    <>
      <Background>
        <Container maxWidth="lg" fixed>
          {/* v5+ Grid: `item` is gone and the breakpoint props collapse into
              `size`. No `spacing` on this container, so widths are unchanged. */}
          <Grid container>
            <Grid size={{ xs: 12, md: 7 }} sx={centerBoxLeftSx}>
              <Box sx={appTitleContainerSx}>
                <Box sx={appIconSx}>
                  <AppIconGraphic
                    src={
                      "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/app_logo_200_dark.jpg"
                    }
                    alt="FarmFLiX App Icon"
                  />
                </Box>
                <Box>
                  <Typography variant="h2" sx={titleSx}>
                    Install The
                  </Typography>
                  <Typography variant="h2" sx={titleLargeSx}>
                    Free App
                  </Typography>
                  {subline && (
                    <Typography variant="h3" sx={sublineSx}>
                      {subline}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={downloadTextContainerSx}>
                {phoneOrTablet && isIOS && (
                  <a
                    href={hrefs.ios}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <Button
                      size="large"
                      variant="outlined"
                      sx={installAppButtonSx}
                    >
                      INSTALL THE APP
                    </Button>
                  </a>
                )}
                {phoneOrTablet && isAndroid && (
                  <a
                    href={hrefs.android}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <Button
                      size="large"
                      variant="outlined"
                      sx={installAppButtonSx}
                    >
                      INSTALL THE APP
                    </Button>
                  </a>
                )}
                <Typography variant="h4" sx={downloadTextSx}>
                  {phoneOrTablet ? "or download at:" : "Download at:"}
                </Typography>
                <a
                  href={hrefs.appStore}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <AppStoreLogo
                    src={appleappstore}
                    alt="Apple App Store Graphic"
                  />
                </a>
                <a
                  href={hrefs.playStore}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <AppStoreLogo
                    src={googleplaystore}
                    alt="Google Play Store Graphic"
                  />
                </a>
                <Typography variant="h4" sx={purchaseTextSx}>
                  Free Download. No Payment Required.
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }} sx={centerBoxRightSx}>
              <SkinWrapper>
                {/* Visible MD, L, XL — was <Hidden smDown> */}
                {mdUp && (
                  <AppImage
                    src={
                      "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/device_skin_100.png"
                    }
                    alt="FarmFLiX App"
                  />
                )}
                {/* Visible XS, SM — was <Hidden mdUp> */}
                {!mdUp && (
                  <AppImageHalf
                    src={
                      "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/device_skin_75.png"
                    }
                    alt="FarmFLiX App"
                  />
                )}
                <DashboardScroller>
                  <Carousel
                    useKeyboardArrows={true}
                    showArrows={false}
                    interval={3000}
                    showThumbs={false}
                    infiniteLoop={true}
                    showStatus={false}
                    autoPlay={true}
                    showIndicators={false}
                    emulateTouch={false}
                    swipeable={false}
                    transitionTime={500}
                  >
                    <Box sx={imageContainerSx}>
                      <img
                        src={
                          "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/1.png"
                        }
                        alt="App Dashboard"
                      />
                    </Box>
                    <Box sx={imageContainerSx}>
                      <img
                        src={
                          "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/2.png"
                        }
                        alt="App Currently Playing"
                      />
                    </Box>
                    <Box sx={imageContainerSx}>
                      <img
                        src={
                          "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/FreeTrial/4.png"
                        }
                        alt="Clydes Farm Series"
                      />
                    </Box>
                  </Carousel>
                </DashboardScroller>
              </SkinWrapper>
            </Grid>
          </Grid>
        </Container>
      </Background>
    </>
  );
};

export default HeroLayout;
